import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { PalletShippingForm } from "./pallet-shipping-form";
import { PalletsListItem } from "../pallets-list/pallets-list-item";
import { usePalletShippingContextDispatcher } from "./pallet-shipping-context";
import { DialogTitle } from "@mui/material";
import { t } from "i18next";
import { useEffectAsync } from "../../../../common/hooks/use-effect-async";
import { context } from "../../../../context/context";
import { Permission } from "../../../../context/permission";

interface IPalletShippingDetailsController {
    palletsList: PalletsListItem[];
    onCompleted: () => Promise<void>;
    onCancel: () => void;
}

interface IPalletShippingDetailsTitleProps {
    isMultiStepShipment: boolean;
    totalNumberOfPallets: number;
    numberOfSteps: number;
    currentShippingStep: number;
    palletsList: PalletsListItem[];
}

export const PalletShippingDetailsController = (props: IPalletShippingDetailsController) => {
    const [remainingPallets, setRemainingPallets] = useState(props.palletsList);
    const [currentPalletsList, setCurrentPalletsList] = useState(PalletShippingPalletsProvider(props.palletsList));

    const isMultiPalletShipping = useMemo(() =>
            props.palletsList.every(p => !p.isVirtualPallet)
            && props.palletsList.find(p => p.carrier !== props.palletsList[0].carrier) !== undefined
            && context.hasPermission(Permission.FeaturePalletPickupSlotShipping),
        [props.palletsList]);
    const numberOfShippingSteps = useMemo(() => isMultiPalletShipping ?
            new Set(props.palletsList.map(p => p.carrier)).size : 1,
        [isMultiPalletShipping]);
    const currentShippingStep = useMemo(() => isMultiPalletShipping ?
        numberOfShippingSteps - new Set(remainingPallets.map(p => p.carrier)).size + 1 : 0,
        [isMultiPalletShipping, numberOfShippingSteps, remainingPallets]);

    const palletShippingContextDispatcher = usePalletShippingContextDispatcher();

    const handleStepCompleted = useCallback(async (palletsList: PalletsListItem[]) => {
        const shippedIdentifiers = palletsList.map(p => p.id);
        setRemainingPallets(prev => prev.filter(p => !shippedIdentifiers.includes(p.id)));
    }, [props.onCompleted]);

    useEffect(() => {
        palletShippingContextDispatcher({ action: "reset" });
    }, [currentPalletsList]);

    useEffectAsync(async () => {
        if (remainingPallets.length > 0) {
            setCurrentPalletsList(PalletShippingPalletsProvider(remainingPallets));
        } else {
            await props.onCompleted();
        }
    }, [remainingPallets]);

    return <PalletShippingForm title={<PalletShippingDetailsTitle isMultiStepShipment={isMultiPalletShipping}
                                                                  totalNumberOfPallets={props.palletsList.length}
                                                                  currentShippingStep={currentShippingStep}
                                                                  numberOfSteps={numberOfShippingSteps}
                                                                  palletsList={remainingPallets} />}
                               hasNextShippingStep={isMultiPalletShipping && currentShippingStep !== numberOfShippingSteps}
                               palletsList={currentPalletsList}
                               isTruckIdentifierRequired={false}
                               onStepCompleted={handleStepCompleted}
                               onCancel={props.onCancel} />;
};

const PalletShippingPalletsProvider = (palletsList: PalletsListItem[]) => {
    if (!context.hasPermission(Permission.FeaturePalletPickupSlotShipping)) {
        return palletsList;
    }

    return !palletsList[0].isVirtualPallet ?
        palletsList.filter(p => p.carrier === palletsList[0].carrier) :
        palletsList;
};

export const PalletShippingDetailsTitle = (props: IPalletShippingDetailsTitleProps) => {
    if (props.palletsList.length === 0) {
        return <Fragment />;
    }

    return !props.isMultiStepShipment ?
        <DialogTitle>
            {props.totalNumberOfPallets > 1 ?
                t("components.pallet.shipping.multiplePalletsTitle", { count: props.totalNumberOfPallets }) :
                t("components.pallet.shipping.singlePalletTitle", { palletCode: props.palletsList[0].code })}
        </DialogTitle> :
        <DialogTitle>
            {t("components.pallet.shipping.multiCarrierPalletsTitle", {
                carrier: props.palletsList[0].carrier,
                currentStep: props.currentShippingStep,
                numberOfSteps: props.numberOfSteps,
            })}
        </DialogTitle>;
};
